import { TamaroConfig } from '@api/raisenow';
import * as Data from 'effect/Data';

/** Source of a tamaro message. */
export enum MessageSource {
  /** Host window that hosts the iframe running Tamaro. */
  Host = 'host',
  /** Iframe window that runs Tamaro. */
  Iframe = 'iframe',
  /** Tamaro itself. */
  Tamaro = 'tamaro',
}

export enum MessageType {
  /** Dispatches an action. */
  Action = 'action',
  /** Some error occured. */
  Error = 'error',
  /** An event was called. */
  Event = 'event',
  /** A result message. */
  Result = 'result',
}

/**
 * Actions that the tamaro iframe can handle.
 */
export enum IframeAction {
  ValidateAndSend = 'payment_form.validate_and_send',
}

export enum Result {
  EpmsSuccess = 'epms_success',
  EpmsFailed = 'epms_failed',
}

/**
 * Events dispatched from the tamaro instance.
 *
 * @see {@link https://unpkg.com/@raisenow/tamaro-core@2.6.0/dist/readme.html#/?id=events}
 */
export enum TamaroEvent {
  AfterRender = 'afterRender',
  PaymentValidateError = 'paymentValidateError',
  BeforePaymentValidateAndSend = 'beforePaymentValidateAndSend',
  BeforePaymentSend = 'beforePaymentSend',
  FetchPaymentDataStart = 'fetchPaymentDataStart',
  FetchPaymentDataEnd = 'fetchPaymentDataEnd',
}

interface BaseMessage {
  /** Message type */
  readonly type: MessageType;
  /** Source of the message */
  readonly source: MessageSource;
  /** Name that identifies the message */
  readonly name: string;
}
export interface MessageFromTamaro extends BaseMessage {
  readonly source: MessageSource.Tamaro;
}
export interface MessageFromIframe extends BaseMessage {
  readonly source: MessageSource.Iframe;
}
export interface MessageFromHost extends BaseMessage {
  readonly source: MessageSource.Host;
}
export type MessageToHost = MessageFromTamaro | MessageFromIframe;
export type Message = MessageToHost | MessageFromHost;

export type WidgetConfig = Readonly<TamaroConfig>;

/**
 * In order to use the Tamaro-Widget for payment, both
 * an instance and the configuration need to be passed
 * to the service.
 */
export interface IframeConfig {
  /**
   * The instance is composed of organization-name and the version used
   */
  readonly tamaroInstance: string;
  /**
   * The config hold all configurations for a customer
   */
  readonly tamaroConfig: WidgetConfig;
  /**
   * When set to `true`, this will cleanup any session data from previous
   * payment attempts.
   */
  readonly cleanupSession: boolean;
}

export class TamaroError extends Error {
  override name = 'TamaroError';
  constructor(
    public readonly source: MessageSource.Iframe | MessageSource.Tamaro,
    message: string,
  ) {
    super(`from ${source}: ${message}`);
  }
}

/**
 * Used for errors that occur in the Tamaro host.
 */
export class HostError extends Data.TaggedError('Tamaro.HostError')<{
  reason: string;
}> {}
